'use client';

import {
  useCallback, useContext, useEffect, useMemo, useRef, useState, KeyboardEvent, ReactNode,
} from 'react';
import { useTranslation } from 'next-i18next';
import {
  AdjustmentsHorizontalIcon, ChevronDownIcon, HeartIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { MicrophoneIcon } from '@heroicons/react/24/solid';
import { Anchor, Button } from '@components/common';
import { getI18nLinkHref } from '@services/i18nPaths';
import { PopoverStyled, PopoverStyledButton, PopoverStyledPanel } from '@components/common/Popover/PopoverStyled';
import { FilterDrawerContext } from '@components/contexts/FilterDrawerContext';
import { HeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import { useSidebarNavigation, type CandidateSearchFilters } from '@components/contexts/SidebarNavigationContext';
import { countAppliedFilters } from '@components/features/filters/helpers/countAppliedFilters';
import { MapButton } from '@components/layout/Header/MainNavbar/search/MapButton';
import { useSwrNextJobs } from '@hooks/useSwrApi/endpoints/next-jobs';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { useAutoResizeTextarea } from '@hooks/useAutoResizeTextarea';
import { slugify } from '@utils/strings/slugify';

interface SearchBranchProps {
  locale: Locale;
  onSubmit: (filters: CandidateSearchFilters) => void;
  leftSlot?: ReactNode;
  missionChip?: ReactNode;
}

// Compare slugified locations — URL-derived values arrive lowercased + de-diacriticised
// (`bucuresti`) while saved alerts keep the canonical form (`București`).
const sameLocations = (a?: string[] | null, b?: string[] | null): boolean => {
  const left = (a ?? []).map(slugify).sort();
  const right = (b ?? []).map(slugify).sort();
  if (left.length !== right.length) return false;
  return left.every((value, i) => value === right[i]);
};

export const SearchBranch = ({ locale, onSubmit, leftSlot, missionChip }: SearchBranchProps) => {
  const { t } = useTranslation('common');
  const { currentView } = useSidebarNavigation();

  const initialFilters = useMemo<CandidateSearchFilters | undefined>(() => {
    if (typeof currentView === 'object' && 'candidateSearch' in currentView) return currentView.candidateSearch;
    if (typeof currentView === 'object' && 'editSearch' in currentView) return currentView.editSearch;
    if (typeof currentView === 'object' && 'keyword' in currentView) return { keyword: currentView.keyword };
    return undefined;
  }, [currentView]);

  const filterDrawer = useContext(FilterDrawerContext);
  const headerSearch = useContext(HeaderSearchContext);
  const appliedFiltersCount = useMemo(
    () => countAppliedFilters(headerSearch?.queryParams),
    [headerSearch?.queryParams],
  );

  // Recover the original phrasing from saved alerts when the URL only carries the
  // slugified keyword (deep links, shared URLs, refreshes). Saved alerts are a
  // logged-in concept — skip the request for guests (/v1/user/next-jobs 401s).
  const { isAuth } = useAuthClient();
  const { data: nextJobs } = useSwrNextJobs({ shouldFetch: isAuth });
  const fallbackAlert = useMemo(() => {
    if (initialFilters?.originalKeyword) return undefined;
    if (!nextJobs || !initialFilters?.keyword) return undefined;
    const targetTitle = slugify(initialFilters.keyword);
    return nextJobs.find((item) => (
      item.sourceType === 'job_search_alert'
      && slugify(item.jobTitle) === targetTitle
      && (item.salary ?? undefined) === initialFilters.salary
      && sameLocations(item.locations, initialFilters.locations)
    ));
  }, [nextJobs, initialFilters]);

  const effectiveOriginalKeyword = initialFilters?.originalKeyword ?? fallbackAlert?.originalKeyword ?? undefined;
  const initialKeyword = effectiveOriginalKeyword ?? initialFilters?.keyword ?? '';

  const [keyword, setKeyword] = useState(initialKeyword);
  const [isEditing, setIsEditing] = useState(false);
  const userEditedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { resize } = useAutoResizeTextarea({ ref: textareaRef, value: keyword, maxRows: 3 });

  useEffect(() => {
    if (!isEditing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
    resize();
  }, [isEditing, resize]);

  // Collapse back to the fake-input view when the user clears their text.
  const prevKeywordRef = useRef(keyword);
  useEffect(() => {
    if (isEditing && prevKeywordRef.current.length > 0 && keyword.length === 0) {
      setIsEditing(false);
    }
    prevKeywordRef.current = keyword;
  }, [isEditing, keyword]);

  // Keep the textarea in sync with the view's keyword: the original phrasing when we have it
  // (saved alerts, or the async fallback-alert lookup), otherwise the keyword carried by the URL
  // (e.g. company/location interest edits, which aren't saved alerts). Skip once the user has
  // started typing so we don't clobber their input.
  useEffect(() => {
    if (userEditedRef.current) return;
    if (!initialKeyword) return;
    if (keyword === initialKeyword) return;
    setKeyword(initialKeyword);
  }, [initialKeyword, keyword]);

  const submit = useCallback(() => {
    onSubmit({ keyword: keyword.trim() });
  }, [keyword, onSubmit]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }, [submit]);

  // Web Speech API — populates the keyword field from the user's voice.
  // Chrome/Edge/Safari support; on unsupported browsers the button no-ops.
  interface SRAlternative { transcript: string }
  interface SRResult { 0: SRAlternative; length: number }
  interface SRResultList { length: number; [index: number]: SRResult }
  interface SREvent { results: SRResultList }
  interface SRInstance {
    lang: string;
    interimResults: boolean;
    continuous: boolean;
    onresult: ((event: SREvent) => void) | null;
    onend: (() => void) | null;
    onerror: (() => void) | null;
    start: () => void;
    stop: () => void;
  }
  type SRCtor = new () => SRInstance;
  type WindowWithSR = Window & {
    SpeechRecognition?: SRCtor;
    webkitSpeechRecognition?: SRCtor;
  };

  const recognitionRef = useRef<SRInstance | null>(null);
  const latestTranscriptRef = useRef('');
  const [isRecording, setIsRecording] = useState(false);

  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(() => {
    if (typeof window === 'undefined') return;
    const w = window as WindowWithSR;
    const SR = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!SR) return;

    latestTranscriptRef.current = '';
    const recognition: SRInstance = new SR();
    recognition.lang = locale === 'en' ? 'en-US' : `${locale}-${locale.toUpperCase()}`;
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = (event: SREvent) => {
      const parts: string[] = [];
      for (let i = 0; i < event.results.length; i += 1) {
        parts.push(event.results[i][0]?.transcript ?? '');
      }
      const transcript = parts.join(' ').trim();
      if (transcript) {
        latestTranscriptRef.current = transcript;
        userEditedRef.current = true;
        setKeyword(transcript);
      }
    };
    recognition.onend = () => {
      setIsRecording(false);
      const finalTranscript = latestTranscriptRef.current.trim();
      if (finalTranscript) {
        onSubmit({ keyword: finalTranscript });
      }
    };
    recognition.onerror = () => { setIsRecording(false); };
    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  }, [locale, onSubmit]);

  const handleMicClick = useCallback(() => {
    if (isRecording) stopRecording();
    else startRecording();
  }, [isRecording, startRecording, stopRecording]);

  const hasText = keyword.trim().length > 0;

  const micPopupItem = (
    <PopoverStyledButton
      as="button"
      type="button"
      onClick={handleMicClick}
      aria-label={isRecording ? t('global.label.stop', 'Stop') : t('global.label.voice', 'Voice')}
      title={isRecording ? t('global.label.stop', 'Stop') : t('global.label.voice', 'Voice')}
      className={`flex !h-9 !w-9 items-center justify-center !rounded-lg !p-0 transition-transform hover:scale-110 lg:!h-10 lg:!w-10 ${isRecording ? '!bg-red-500 hover:!bg-red-500' : 'bg-secondary hover:!bg-secondary'}`}
    >
      <MicrophoneIcon className="size-5 text-white" />
    </PopoverStyledButton>
  );

  const searchPopupItem = (
    <PopoverStyledButton
      as="button"
      type="submit"
      form="candidate-prompt-bar-search"
      aria-label={t('mc.search.job')}
      title={t('mc.search.job')}
      className="flex !h-9 !w-9 items-center justify-center !rounded-lg bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary lg:!h-10 lg:!w-10"
    >
      <MagnifyingGlassIcon className="size-5 text-white" />
    </PopoverStyledButton>
  );

  const moreOptionsPopup = (
    <PopoverStyled className="relative ml-2 shrink-0">
      <PopoverStyledButton
        as="button"
        type="button"
        aria-label={t('global.label.more.options', 'More options')}
        className="relative flex size-7 items-center justify-center rounded-md text-ink-medium transition-colors hover:bg-surface-100 hover:text-ink-medium data-[open]:bg-surface-100"
      >
        <ChevronDownIcon className="size-4" />
        {appliedFiltersCount > 0 && (
          <span
            aria-hidden
            className="pointer-events-none absolute right-0.5 top-0.5 size-1.5 rounded-full bg-ink-medium"
          />
        )}
      </PopoverStyledButton>

      <PopoverStyledPanel
        animation="slideUp"
        position="right"
        className="absolute bottom-full right-0 mb-2 flex items-center gap-2 rounded-xl border border-input bg-surface p-2 shadow-lg"
      >
        <>
          {hasText ? micPopupItem : searchPopupItem}

          {filterDrawer && (
            <div className="relative shrink-0">
              <PopoverStyledButton
                as="button"
                type="button"
                onClick={() => { filterDrawer.toggleDrawer(); }}
                aria-label={t('global.label.filter')}
                title={t('global.label.filter')}
                className="flex !h-9 !w-9 items-center justify-center !rounded-lg !bg-transparent !p-0 !text-ink-medium hover:!bg-surface-100 hover:!text-ink-medium lg:!h-10 lg:!w-10"
              >
                <AdjustmentsHorizontalIcon className="size-5" />
              </PopoverStyledButton>
              {appliedFiltersCount > 0 && (
                <span
                  aria-label={t('global.label.filter')}
                  className="pointer-events-none absolute right-1 top-1 size-2 rounded-full bg-ink-medium"
                />
              )}
            </div>
          )}

          <MapButton
            locale={locale}
            rounding="none"
            className="!h-9 !w-9 shrink-0 !rounded-lg !bg-transparent !p-0 !text-ink-medium hover:!bg-surface-100 hover:!text-ink-medium lg:!h-10 lg:!w-10"
            iconClassName="size-5"
          />

          {filterDrawer && (
            <Anchor
              href={getI18nLinkHref('/applied-jobs?pageNr=1', locale)}
              styling="none"
              aria-label={t('header.menu.applied_jobs')}
              title={t('header.menu.applied_jobs')}
              className="flex !h-9 !w-9 shrink-0 items-center justify-center !rounded-lg !bg-transparent !p-0 !text-ink-medium hover:!bg-surface-100 hover:!text-ink-medium lg:!h-10 lg:!w-10"
            >
              <HeartIcon className="size-5" />
            </Anchor>
          )}
        </>
      </PopoverStyledPanel>
    </PopoverStyled>
  );

  const primaryButton = hasText ? (
    <Button
      type="submit"
      form="candidate-prompt-bar-search"
      styling="outline"
      color="light"
      rounding="none"
      // Prevent textarea blur when clicking — without this, blur collapses the
      // editing UI before the click reaches the submit button.
      onMouseDown={(e) => e.preventDefault()}
      aria-label={t('mc.search.job')}
      title={t('mc.search.job')}
      className="!h-9 !w-9 shrink-0 !rounded-lg bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary lg:!h-10 lg:!w-10"
    >
      <MagnifyingGlassIcon className="size-5 text-white" />
    </Button>
  ) : (
    <Button
      type="button"
      styling="outline"
      color="light"
      rounding="none"
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleMicClick}
      aria-label={isRecording ? t('global.label.stop', 'Stop') : t('global.label.voice', 'Voice')}
      title={isRecording ? t('global.label.stop', 'Stop') : t('global.label.voice', 'Voice')}
      className={`!h-9 !w-9 shrink-0 !rounded-lg !p-0 transition-transform hover:scale-110 lg:!h-10 lg:!w-10 ${isRecording ? '!bg-red-500 hover:!bg-red-500' : 'bg-secondary hover:!bg-secondary'}`}
    >
      <MicrophoneIcon className="size-5 text-white" />
    </Button>
  );

  const actionButtons = (
    <div className="ml-auto flex items-center gap-1">
      {primaryButton}

      {moreOptionsPopup}
    </div>
  );

  if (!isEditing) {
    return (
      <form
        id="candidate-prompt-bar-search"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="flex w-full items-center gap-2"
      >
        {leftSlot}
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          aria-label={t('extended.keyword.placeholder')}
          className="flex min-w-0 flex-1 px-2 py-1 text-left text-xs leading-normal md:text-sm"
        >
          {keyword
            ? <span className="block w-full truncate text-ink">{keyword}</span>
            : <span className="block w-full truncate text-ink/60">{t('extended.keyword.placeholder')}</span>}
        </button>
        {actionButtons}
      </form>
    );
  }

  return (
    <form
      id="candidate-prompt-bar-search"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      className="flex w-full flex-col gap-1"
    >
      <textarea
        ref={textareaRef}
        id="candidate_prompt_bar_keyword"
        name="candidate_prompt_bar_keyword"
        rows={1}
        value={keyword}
        onChange={(e) => { userEditedRef.current = true; setKeyword(e.target.value); }}
        onKeyDown={handleKeyDown}
        onBlur={() => setIsEditing(false)}
        aria-label={t('extended.keyword.placeholder')}
        className="block w-full resize-none border-none bg-transparent px-2 py-1 text-base leading-normal text-ink shadow-none outline-none focus:ring-0 focus:ring-offset-0 md:text-sm"
      />

      <div className="flex w-full items-center gap-2">
        {leftSlot}
        {missionChip}
        {actionButtons}
      </div>
    </form>
  );
};
